import { useSearchOpenContext } from '@upright/ui/common/generic/components/bou-tool-header/use-search-open-context';
import { KeyboardEvent, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Select, { SelectInstance } from 'react-select';
import { FeatureId } from '../../../../../../../common/iam/access/feature-access/types';
import { ModelEntityType } from '../../../../../../../common/model/input/types';
import {
  AdditionalSearchType,
  defaultAllSearchScope,
  HeaderSearchSelection,
  internalAllSearchScope,
  ModelEntitySearchResponseItem,
  SearchEntityType,
} from '../../../../../../../common/model/search/types';
import { isTeamUser } from '../../../../../../../common/utils';
import { loggedInUser } from '../../../../../../untidy/redux/selectors';
import { useHasAccessToFeature } from '../../../../../generic/hooks';
import { saveSearchFilter } from '../../../../../generic/utilities/storage';
import { CustomInput } from '../components/custom-input';
import { getCustomOption } from '../components/custom-option';
import { CustomSingleValue } from '../components/single-value';
import { SearchTheme } from '../types';
import { isSearchTriggeredOnInput, useEntitySearch } from '../use-entity-search';
import { useTrackableSearchInputs } from '../use-trackable-inputs';
import { getMenuList } from './components/menu-list';
import { HeaderSearchValueContainer } from './components/value-container';
import { useHeaderSearchContext } from './context';
import { selectStyles } from './select-styles';
import { useFocusHotkey } from './use-focus-hotkey';
import { useHeaderSearchTypeSelection } from './use-header-search-type';
import { useSuggestions } from './use-suggestions';

interface Props {
  onChange: (value?: ModelEntitySearchResponseItem) => void;
  selectedValue?: ModelEntitySearchResponseItem;
  hideFilters?: boolean;
  theme: SearchTheme;
}

export const HeaderSearch = (props: Props) => {
  const { selectedValue, hideFilters, onChange } = props;
  const user = useSelector(loggedInUser);

  const selectRef = useRef<SelectInstance<ModelEntitySearchResponseItem>>(null);
  useFocusHotkey(selectRef);

  const headerSearchOpenContext = useSearchOpenContext();
  useEffect(() => {
    if (headerSearchOpenContext.headerSearchMenuOpen) {
      if (selectRef.current) {
        selectRef.current.focus();
      }
    }
  }, [headerSearchOpenContext.headerSearchMenuOpen]);

  const ctx = useHeaderSearchContext();
  const { headerSearchType, setHeaderSearchType } = useHeaderSearchTypeSelection();
  ctx.filter = headerSearchType;
  const searchTypeInfo = mapFilterSelectionToSearchTypes(hideFilters, headerSearchType, isTeamUser(user));

  const { input, setInput, sendAttempts } = useTrackableSearchInputs(false);

  // When header type changes, send the attempts with the current type information
  const setHeaderSearchTypeAndSendAttempts = (newHeaderSearchType: HeaderSearchSelection) => {
    sendAttempts({ headerSearchSelection: headerSearchType });
    setHeaderSearchType(newHeaderSearchType);
  };

  const headerSearch = useEntitySearch(input, searchTypeInfo.searchTypes, {
    semanticSearchMode: searchTypeInfo.semanticSearchMode,
    allResultsEnabled: true,
  });

  function handleChange(value: ModelEntitySearchResponseItem | null) {
    if (value) {
      // On option click, send attempts together with the current type information
      sendAttempts({ entityRef: { id: value.id, type: value.type }, headerSearchSelection: headerSearchType });
    }
    onChange(value ?? undefined);
  }

  const suggestions = useSuggestions(headerSearchType);
  const optionsToDisplay: ModelEntitySearchResponseItem[] = headerSearch.preprocessedSearchInput
    ? headerSearch.searchOptions
    : suggestions;

  const canAccessProductView = useHasAccessToFeature(FeatureId.PRODUCT_SEARCH);

  const placeholder = (() => {
    if (canAccessProductView) {
      return `Search for products, companies, or funds`;
    }
    return `Search for companies or funds`;
  })();

  return (
    <Select<ModelEntitySearchResponseItem, false>
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        const key = e.key;
        // Open search shortcut, and closing the search bar with mapping plugin
        if ((e.ctrlKey && key.toLowerCase() === 's') || key === 'Escape') {
          e.stopPropagation();
        }
      }}
      ref={selectRef}
      components={{
        Option: getCustomOption({
          headerSearch: true,
          theme: props.theme,
          user,
          onOption: sendAttempts,
          enableAllResults: true,
        }),
        IndicatorSeparator: EmptyComponent,
        DropdownIndicator: EmptyComponent,
        MenuList: getMenuList({
          filter: headerSearchType,
          onFilterChange: (newType: HeaderSearchSelection) => {
            setHeaderSearchTypeAndSendAttempts(newType);
            saveSearchFilter(newType);
          },
          hideFilters,
          hasSuggestions: !!(suggestions.length && !headerSearch.preprocessedSearchInput),
        }),
        SingleValue: CustomSingleValue,
        Input: CustomInput,
        ValueContainer: HeaderSearchValueContainer,
      }}
      styles={selectStyles}
      value={selectedValue ?? null}
      onChange={handleChange}
      inputValue={input}
      onInputChange={(value, actionMeta) => {
        if (actionMeta.action === 'input-change' || actionMeta.action === 'set-value') {
          setInput(value);
        }
      }}
      options={optionsToDisplay}
      isLoading={
        !!headerSearch.preprocessedSearchInput &&
        (headerSearch.basicQuery.isLoading || headerSearch.semanticQuery.isLoading || headerSearch.isDebouncing)
      }
      getOptionLabel={option => {
        return `${option.name} (${option.type})`;
      }}
      getOptionValue={option => {
        return `${option.name} (${option.type})`;
      }}
      filterOption={null}
      openMenuOnFocus={true}
      blurInputOnSelect={true}
      placeholder={placeholder}
      isMulti={false}
      isClearable={false}
      loadingMessage={() => (headerSearch.errorMessage ? headerSearch.errorMessage : 'Searching…')}
      noOptionsMessage={() => getNoOptionsMessage(headerSearch.preprocessedSearchInput, headerSearch.errorMessage)}
      onBlur={() => {
        sendAttempts({ headerSearchSelection: headerSearchType });
        headerSearchOpenContext.setHeaderSearchMenuOpen(false);
      }}
    />
  );
};

const EmptyComponent = () => <div />;

function getNoOptionsMessage(preprocessedInput: string, fetchError: string | null) {
  if (fetchError) {
    return fetchError;
  }
  if (isSearchTriggeredOnInput(preprocessedInput)) {
    return `No results matching ${preprocessedInput}`;
  }
  if (preprocessedInput.length === 0) {
    return 'Start typing to search...';
  } else {
    return 'Type in a few more letters...';
  }
}

function mapFilterSelectionToSearchTypes(
  hideFilters: boolean | undefined,
  filter: HeaderSearchSelection | undefined,
  isTeam: boolean,
): { searchTypes: readonly SearchEntityType[]; semanticSearchMode: 'none' | 'combine both' | 'only semantic' } {
  if (hideFilters || !filter || filter === AdditionalSearchType.ALL) {
    const showPhenomena = isTeam;
    const allowedSearchTypes = showPhenomena
      ? defaultAllSearchScope
      : defaultAllSearchScope.filter(type => type !== ModelEntityType.PHENOMENON);
    return { searchTypes: allowedSearchTypes, semanticSearchMode: 'none' };
  }
  switch (filter) {
    case AdditionalSearchType.ALL_INTERNAL: {
      return { searchTypes: internalAllSearchScope, semanticSearchMode: 'none' };
    }
    case ModelEntityType.PRODUCT:
      return { searchTypes: [ModelEntityType.PRODUCT, ModelEntityType.MIXIN], semanticSearchMode: 'combine both' };
    case ModelEntityType.MIXIN:
      return {
        searchTypes: [ModelEntityType.MIXIN],
        semanticSearchMode: 'combine both',
      };
    case ModelEntityType.PHENOMENON:
      return {
        searchTypes: [ModelEntityType.PHENOMENON],
        semanticSearchMode: 'combine both',
      };
    default:
      // Company, company group, portfolio
      return { searchTypes: [filter], semanticSearchMode: 'none' };
  }
}
